import { Component, inject, signal } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { ModalService } from '../../../../core/services/modal.service';
import { Load } from '../../../../core/models/load.model';
import { LoadSegment } from '../../../../core/models/load-segment.model';
import { SegmentService } from '../../../../core/services/segment.service';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { LucideIconComponent } from '../../../../shared/ui/lucide-icon/lucide-icon.component';
import { FormatTimePipe } from '../../../../shared/pipes/format-time.pipe';
import { Stop } from '../../../../core/models/stop.model';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { TextComponent } from '../../../../shared/ui/typography/text/text.component';
import { CardComponent } from '../../../../shared/ui/layout/card/card.component';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { LoadingService } from '../../../../shared/ui/loading/loading.service';
import { BadgeComponent } from '../../../../shared/ui/badge/badge.component';

@Component({
  selector: 'app-edit-route-form',
  standalone: true,
  imports: [CommonModule, 
    ReactiveFormsModule,
    DragDropModule,
    LucideIconComponent,
    FormatTimePipe,
    ButtonComponent,
    TextComponent,
    CardComponent,
    BadgeComponent],
  templateUrl: './edit-route-form.component.html',
  styleUrls: ['./edit-route-form.component.css'],
})
export class EditRouteFormComponent {
  private readonly modalService = inject(ModalService);
  private readonly segmentService = inject(SegmentService);
  private readonly toastService = inject(ToastService);
  private readonly loadingService = inject(LoadingService);
  private readonly fb = inject(FormBuilder);

  protected readonly load: Load = this.modalService.state()!.data.load;
  protected readonly existingSegments: LoadSegment[] | undefined =
    this.modalService.state()!.data.segments;
  protected readonly onSuccess = this.modalService.state()!.data.onSuccess;
  protected readonly isSaving = signal(false);
  protected form: FormGroup;

  constructor() {
    this.form = this.fb.group({
      stops: this.fb.array([]),
    });

    // Initialize form with data from the load
    this.initializeForm();
  }

  private initializeForm(): void {
    // Clear any existing controls
    this.stops.clear();

    // Add a control for each stop in the load
    for (const stop of this.load.stops) {
      this.stops.push(this.fb.control({ ...stop }));
    }
  }

  // --- FormArray Getters ---
  get stops(): FormArray {
    return this.form.get('stops') as FormArray;
  }

  protected getStopIcon(stopType?: string | null): string {
    switch (stopType) {
      case 'pickup':
        return 'ArrowUp';
      case 'delivery':
        return 'ArrowDown';
      case 'both':
        return 'ArrowUpDown';
      case 'handoff':
        return 'ArrowRightLeft';
      case 'yard_drop':
      case 'yard_hook':
        return 'Warehouse';
      default:
        return 'MapPin';
    }
  }

  removeStop(index: number): void {
    if (this.stops.length <= 2) {
      this.toastService.show('warning', 'A load route must keep at least 2 stops.');
      return;
    }

    const target = this.stops.at(index)?.value as Stop | undefined;
    if (target?.status === 'DEPARTED') {
      this.toastService.show('warning', 'Completed (departed) stops cannot be removed.');
      return;
    }

    this.stops.removeAt(index);

    // Re-number all stops after removal for preview (original identities reconcile via ItineraryReconciler)
    this.stops.controls.forEach((ctrl, idx) => {
      const stop = { ...(ctrl.value as Stop), stopNumber: idx + 1 };
      ctrl.setValue(stop, { emitEvent: false });
    });
  }

  cancel(): void {
    this.modalService.close();
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.stops.length < 2) {
      this.toastService.show('warning', 'A route must contain at least 2 stops.');
      return;
    }

    this.isSaving.set(true);

    this.loadingService.show({
      variant: 'overlay',
      title: 'Reconciling Route & Legs',
      subtitle: 'Updating stops, leg boundaries, and route mileage...',
    });

    const updatedStops = this.stops.controls.map((ctrl) => ({ ...ctrl.value }));

    try {
      await this.segmentService.saveEditedRouteAndReconcile(
        this.load,
        updatedStops,
        undefined,
        this.existingSegments
      );
      this.loadingService.hide();
      this.modalService.close();
      this.onSuccess?.();
      setTimeout(() => {
        this.toastService.show('success', 'Route and leg assignments reconciled successfully', {
          title: 'Route Updated',
          duration: 4000,
        });
      }, 100);
    } catch (error) {
      console.error('Failed to update route:', error);
      this.loadingService.hide();
      this.toastService.show(
        'error',
        'Failed to update route. Please try again.',
        {
          title: 'Update Failed',
          showClose: true,
          duration: 0,
        }
      );
    } finally {
      this.isSaving.set(false);
    }
  }

  drop(event: CdkDragDrop<string[]>): void {
    // Move the control in the array
    moveItemInArray(
      this.stops.controls,
      event.previousIndex,
      event.currentIndex
    );
    this.stops.updateValueAndValidity();

    // Update stopNumber for each stop to match its new index
    this.stops.controls.forEach((ctrl, idx) => {
      const stop = ctrl.value as Stop;
      if (stop) {
        stop.stopNumber = idx + 1; // Assuming stopNumber starts at 1
        ctrl.setValue(stop, { emitEvent: false });
      }
    });
  }
}
